import React, { useEffect, useState } from "react";
import { fetchRegistrationSettings, publishRegistrationSettings, previewRegistrationSettings, registrationError } from "../../api/customerRegistrationApi";
import FormPreview from "./forms/FormPreview";

const SECTIONS = ["Patient details", "Referral source", "Emergency contact", "Reason for visit", "Medical history", "Additional medical information", "Signer details"];
const YES_NO = [{ value: "yes", label: "Yes" }, { value: "no", label: "No" }];
const protectedQuestion = (question) => ["signer_name", "signer_relationship"].includes(question.key);
const input = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm";
const button = "rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold disabled:opacity-50";
const questionType = (question) => question.type === "select"
  && JSON.stringify(question.options) === JSON.stringify(YES_NO) ? "yes_no" : question.type;

export default function RegistrationFormSettings({ botId }) {
  const [settings, setSettings] = useState(null);
  const [version, setVersion] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [preview, setPreview] = useState(null);
  const [newLabel, setNewLabel] = useState("");
  const [newType, setNewType] = useState("text");
  const [newSection, setNewSection] = useState("Patient details");
  const [reload, setReload] = useState(0);
  useEffect(() => {
    let active = true;
    setSettings(null);
    setError("");
    fetchRegistrationSettings(botId).then((response) => {
      if (active) { setSettings(response.data.settings); setVersion(response.data.template_version_id); }
    }).catch((err) => { if (active) setError(registrationError(err)); });
    return () => { active = false; };
  }, [botId, reload]);
  const update = (key, changes) => {
    setNotice("");
    setSettings((current) => ({ ...current, questions: current.questions.map((question) =>
      question.key === key ? { ...question, ...changes } : question) }));
  };
  const execute = async (mode) => {
    setBusy(true); setError(""); setNotice("");
    try {
      const response = await (mode === "publish" ? publishRegistrationSettings : previewRegistrationSettings)(botId, settings);
      if (mode === "publish") {
        setSettings(response.data.settings); setVersion(response.data.template_version_id);
        setNotice("Published. New registrations now use this version; existing signed forms are unchanged.");
      } else setPreview(response.data);
    } catch (err) { setError(registrationError(err)); }
    finally { setBusy(false); }
  };
  const addQuestion = () => {
    if (!newLabel.trim()) { setError("Enter a question label."); return; }
    let number = settings.questions.length + 1;
    const keys = new Set(settings.questions.map((question) => question.key));
    while (keys.has(`question_${number}`)) number += 1;
    const type = newType === "yes_no" ? "select" : newType;
    setSettings((current) => ({ ...current, questions: [...current.questions, {
      key: `question_${number}`, label: newLabel.trim(), type, section: newSection,
      enabled: true, required: false, patient_editable: true,
      options: newType === "yes_no" ? YES_NO : [],
      validation: { max_length: type === "textarea" ? 2000 : 200 },
    }] }));
    setNewLabel(""); setNotice(""); setError("");
  };
  const move = (key, direction) => setSettings((current) => {
    const questions = [...current.questions];
    const index = questions.findIndex((question) => question.key === key);
    const target = index + direction;
    if (target < 0 || target >= questions.length || questions[target].section !== questions[index].section) return current;
    [questions[index], questions[target]] = [questions[target], questions[index]];
    return { ...current, questions };
  });
  return <section aria-label="Registration form settings" className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6">
    <h2 className="text-lg font-bold">Registration form settings</h2>
    <p className="text-sm text-slate-600">Start with the prebuilt patient form. Show/hide fields, edit questions and publish for this clinic only. Patient identity, OTP verification, acceptance and signature remain protected.</p>
    {!settings && !error && <p role="status">Loading registration settings...</p>}
    {error && <div role="alert" className="text-sm text-rose-700">{error} {!settings && <button type="button" className={button} onClick={() => setReload((value) => value + 1)}>Retry</button>}</div>}
    {notice && <p role="status" className="text-sm text-emerald-700">{notice}</p>}
    {settings && <fieldset disabled={busy} className="space-y-5">
      <p className="text-sm">{version ? `Published version reference: ${version}` : "Not published yet. Publish the prebuilt form to enable signed registration."}</p>
      <label className="block text-sm font-semibold">Clinic name
        <input className={input} maxLength={200} value={settings.ui_config.clinic_name || ""} placeholder="Use clinic branding"
          onChange={(event) => setSettings((current) => ({ ...current, ui_config: { ...current.ui_config, clinic_name: event.target.value || null, clinic_name_runs: null } }))} />
      </label>
      {SECTIONS.map((section) => {
        const questions = settings.questions.filter((question) => question.section === section);
        return <details key={section} className="rounded-lg border border-slate-200 p-3" open={section === "Medical history"}>
          <summary className="cursor-pointer font-semibold">{section} ({questions.filter((question) => question.enabled).length} shown)</summary>
          <div className="mt-3 space-y-3">
            <button type="button" className={button} onClick={() => setSettings((current) => ({
              ...current, questions: current.questions.map((question) => question.section === section && !protectedQuestion(question)
                ? { ...question, enabled: !questions.some((item) => item.enabled && !protectedQuestion(item)) } : question),
            }))}>Show / hide optional section</button>
            {questions.map((question) => <div key={question.key} className="grid gap-2 rounded-lg bg-slate-50 p-3 sm:grid-cols-[1fr_auto]">
              <label className="text-xs font-semibold">Question label
                <input aria-label={`Label for ${question.key}`} className={input} maxLength={200} value={question.label}
                  onChange={(event) => update(question.key, { label: event.target.value })} />
              </label>
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <label><input type="checkbox" aria-label={`Show ${question.label}`} checked={question.enabled} disabled={protectedQuestion(question)}
                  onChange={(event) => update(question.key, { enabled: event.target.checked })} /> Show</label>
                <label><input type="checkbox" aria-label={`Require ${question.label}`} checked={question.key === "signer_relationship" ? false : question.required} disabled={protectedQuestion(question)}
                  onChange={(event) => update(question.key, { required: event.target.checked })} /> Required</label>
                <span className="text-xs text-slate-500">{questionType(question) === "yes_no" ? "Yes / No" : question.type}</span>
                <button type="button" className={button} aria-label={`Move ${question.label} up`} onClick={() => move(question.key, -1)}>Up</button>
                <button type="button" className={button} aria-label={`Move ${question.label} down`} onClick={() => move(question.key, 1)}>Down</button>
              </div>
              {question.type === "select" && questionType(question) !== "yes_no" && <label className="text-xs sm:col-span-2">Options (one label per line)
                <textarea className={input} value={question.options.map((option) => option.label).join("\n")} onChange={(event) => {
                  const labels = event.target.value.split("\n");
                  update(question.key, { options: labels.map((label, index) => ({
                    label, value: question.options[index]?.value || `option_${index + 1}`,
                  })) });
                }} />
              </label>}
            </div>)}
          </div>
        </details>;
      })}
      <div className="grid gap-3 rounded-lg border border-slate-200 p-4 sm:grid-cols-2">
        <h3 className="font-bold sm:col-span-2">Add question / medical condition</h3>
        <label className="text-sm">New question label<input className={input} maxLength={200} value={newLabel} onChange={(event) => setNewLabel(event.target.value)} /></label>
        <label className="text-sm">Answer type<select className={input} value={newType} onChange={(event) => setNewType(event.target.value)}>
          <option value="text">Short text</option><option value="textarea">Long text</option><option value="yes_no">Yes / No</option>
          <option value="date">Date</option><option value="checkbox">Checkbox</option><option value="select">Dropdown</option>
        </select></label>
        <label className="text-sm">Section<select className={input} value={newSection} onChange={(event) => setNewSection(event.target.value)}>
          {SECTIONS.map((section) => <option key={section}>{section}</option>)}
        </select></label>
        <button type="button" className={button} disabled={settings.questions.length >= 80} onClick={addQuestion}>Add question</button>
      </div>
      <label className="block text-sm font-semibold">Declaration
        <textarea rows={5} className={input} maxLength={1000} value={settings.declaration}
          onChange={(event) => setSettings((current) => ({ ...current, declaration: event.target.value }))} />
      </label>
      <div className="flex gap-3">
        <button type="button" className={button} onClick={() => execute("preview")}>Preview registration form</button>
        <button type="button" className={`${button} bg-cyan-800 text-white`} onClick={() => execute("publish")}>{busy ? "Working..." : "Publish registration form"}</button>
      </div>
    </fieldset>}
    {preview && <FormPreview preview={preview} onClose={() => setPreview(null)} />}
  </section>;
}
